import { LoadingProgress, TextField, Link } from '@framework-controls/core';
import { IView } from 'Framework';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ErrorMessage from 'src/components/ErrorMessage/ErrorMessage';
import { strings } from 'src/localization';
import { ApptCust, DecodeRec, NewVehicle, SpecData, UpdateApptVehInp, VehicleRec, ascCtaInteractionInput, ascFormEngagementInput, ascElementConfigurationInput } from 'src/models';
import css from './VinInput.module.scss';
import WhereIsMyVin from '../../WhereIsMyVin/WhereIsMyVin';
import { ascCtaInteraction, ascFormEngagement, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';

interface IProps {
    leftPaneViewOrder: IView[];
    optionsSelected?: NewVehicle;
    decodeData: DecodeRec;
    desktopView: boolean;
    vehicleSpecs?: SpecData;
    store: string;
    branch: string;
    vehicles: VehicleRec[];
    theme: string;
    dialogOpen: boolean;
    mainPaneViewOrder: IView[];
    selectedVehicle?: string;
    onAddNewVehicle: (state: NewVehicle) => void;
    pushToLeftPane: (inputView: IView) => void;
    popFromLeftPane: (id: string | string[]) => void;
    decodeVin: (id: string, store: string, branch: string, checkOwned: string,
        lastName?: string, phoneNumber?: string, email?: string, nameId?: string) => void;
    apptCust: ApptCust;
    clearRDWVehicle: () => void;
    clearDecodeData: () => void;
    selectVehicle: (id:string) => void;
    updateApptVeh: (inp: UpdateApptVehInp) => void;
    setVinDialogOpen: (inp: boolean) => void;
    clearNewVehicle: () => void;
}

const VinInput = (props: IProps) => {
    const { optionsSelected, onAddNewVehicle } = props;
    const { vin } = optionsSelected!;
    const [showVinExistsError, setShowVinExistsError] = useState(false);
    const [localVin, setLocalVin] = useState(vin);
    const [decodingVin, setDecodingVin] = useState(false);
    const [decodeError, setDecodeError] = useState('');
    const [ownedVinIndex, setOwnedVinIndex] = useState('');

    const updateVin = useCallback((value: string, vinErr?: boolean) => {
        const selectedVehicle = props.vehicles.find(veh => veh.vin === value);
        if (selectedVehicle && !vinErr) {
            setShowVinExistsError(true);
            setOwnedVinIndex(selectedVehicle.id);

            return;
        }
        if (ownedVinIndex) {
            props.selectVehicle(ownedVinIndex);
            const apptVehicleInfo = {
                vin: props.vehicles[ownedVinIndex]!.vin,
                makeCode: props.vehicles[ownedVinIndex].makeCode,
                make: props.vehicles[ownedVinIndex].make,
                model: props.vehicles[ownedVinIndex].model,
                series: props.vehicles[ownedVinIndex].style,
                vehYr: props.vehicles[ownedVinIndex].year,
                vehColor: 'default',
                mileage: props.vehicles[ownedVinIndex].prevMileage,
                imagePath: props.vehicles[ownedVinIndex].imagePath,
            };
            props.updateApptVeh(apptVehicleInfo);
        } else {
            if (value !== optionsSelected?.vin) {
                const options = { ...optionsSelected! };
                options['vin'] = value;
                // If we're changing the vin, set vinValid to false
                onAddNewVehicle({ ...options, estMileage: '', vinValid: false });
            }
            if (value !== '') {
                setDecodingVin(true);
                props.clearDecodeData();
                const lastName = props.apptCust.custLastName ? props.apptCust.custLastName : '';
                const phoneNumber = props.apptCust.custPhoneNumber ? props.apptCust.custPhoneNumber : '';
                const email = props.apptCust.custEmailAddress ? props.apptCust.custEmailAddress : '';
                if (props.apptCust.custNameId) {
                    const strippedPhone = phoneNumber.replace(/[^0-9]/g, '');
                    props.decodeVin(value, props.store, props.branch, 'Y',
                        lastName, strippedPhone, email, props.apptCust.custNameId);
                } else {
                    props.decodeVin(value, props.store, props.branch, 'Y');
                }
                props.clearRDWVehicle();
            }
        }
    }, [props.vehicles, props.selectVehicle, props.clearDecodeData, props.apptCust.custLastName, props.apptCust.custPhoneNumber,
        props.apptCust.custEmailAddress, props.apptCust.custNameId, props.clearRDWVehicle, props.decodeVin, props.store,
        props.branch, optionsSelected, onAddNewVehicle, props.updateApptVeh, ownedVinIndex]);

    const closeVinError = useCallback(() => {
        setShowVinExistsError(false);
    }, []);

    // Update and check the vin automatically if we type our 17th character
    const vinChangedHandler = useCallback((_: string, value: string) => {
        const vinValue = value.trim();
        if (vinValue.length <= 17) {
            setLocalVin(value.toUpperCase());
            if (vinValue.length === 17) {
                updateVin(value.toUpperCase());
            }
        }
    }, [updateVin]);

    // Could swap vin input to a form so enter is automatically handled onSubmit
    const onKeyPressHandler = useCallback((key: string) => {
        if (key === 'Enter') {
            updateVin(localVin);
        }
    }, [updateVin, localVin]);

    useEffect(() => {
        if (decodingVin && (props.decodeData.make === 'ERROR')) {
            setDecodeError(props.decodeData.make);
            setDecodingVin(false);
        } else if (
            vin !== '' &&
            decodingVin &&
            props.decodeData.make &&
            props.decodeData.make !== 'ERROR'
        ) {
            setDecodingVin(false);
            const vehicleInfo = { ...props.optionsSelected! };
            vehicleInfo.vinValid = true;
            vehicleInfo.makeCode = props.decodeData.makeCode;
            vehicleInfo.make = props.decodeData.make;
            vehicleInfo.model = props.decodeData.model;
            vehicleInfo.year = props.decodeData.year;
            if (props.decodeData.decodeErr) {
                vehicleInfo.vinValid = false;
                vehicleInfo.vin = '';
            }
            onAddNewVehicle({ ...vehicleInfo });
            const apptVehicleInfo = {
                vin: vehicleInfo.vin,
                makeCode: vehicleInfo?.makeCode,
                make: vehicleInfo?.make,
                model: vehicleInfo?.model,
                series: vehicleInfo?.trim,
                vehYr: vehicleInfo?.year,
                vehColor: 'default',
                mileage: 0,
                imagePath: '',
            };
            props.updateApptVeh(apptVehicleInfo);
            props.selectVehicle('AddVehicle');
        }
    }, [props.decodeData, props.optionsSelected, onAddNewVehicle, decodingVin, vin, localVin, props.updateApptVeh,
        props.selectVehicle]);

    const closeHandler = useCallback((e: any) => {
        setDecodeError('');
        setLocalVin('');
        props.clearNewVehicle();
        props.clearDecodeData();
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.TryAgain,
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.TryAgain,
            elementType: 'popup',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [props.clearDecodeData, props.clearNewVehicle, props.vehicles, props.selectedVehicle]);

    const GetErrorMessage = useCallback((closeHandler: (e: any) => void) => {
        // sets message default to "Add Vehicle Error" and only changes it if the VIN is unable to be decoded
        let headerMsg = strings.VinCreateError;
        let errorMsg = strings.VinCreateErrorOwned;
        if (decodeError === 'ERROR') {
            headerMsg = strings.VinDecodeError;
            errorMsg = strings.VinDecodeMsg;
        }
        const primaryMessage = strings.TryAgain;

        return (
            <ErrorMessage
                headerMsg={headerMsg}
                errorMsg={errorMsg}
                primaryBtnMessage={primaryMessage}
                secondaryBtnMessage=''
                primaryHandler={closeHandler}
                secondaryHandler={closeHandler}
                closeHandler={closeHandler}
            />
        );
    }, [decodeError]);

    const error = useMemo(() => {
        if (decodeError) {
            setDecodingVin(false);

            return GetErrorMessage(closeHandler);
        }

        return null;
    }, [decodeError, closeHandler, GetErrorMessage]);

    const vinExistsError = useMemo(() => {
        if (showVinExistsError) {
            return GetVinExistsErrorMessage(closeVinError, () => updateVin(localVin, true));
        }

        return null;
    }, [showVinExistsError, closeVinError, updateVin, localVin]);

    const whereHandler = () => {
        props.setVinDialogOpen(!props.dialogOpen);
    };
    const handleVinBlur = useCallback(() => {
        updateVin(localVin);
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'item',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.VIN,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
    }, [localVin, updateVin, props.vehicles, props.selectedVehicle]);

    const interactionHandler = useCallback((e: any) => {
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.WhereIsMyVIN,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'information_link',
            eventAction: 'click',
            eventActionResult: props.dialogOpen ? 'close' : 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.dialogOpen, props.vehicles, props.selectedVehicle]);


    const vinLinkStyles = classNames(
        css.linkButtonThin,
        {
            [css.linkButtonThinBMW]: props.theme === 'bmw',
            [css.linkButtonThinAlt]: props.theme === 'chevrolet' || props.theme === 'infiniti',

        },
    );

    return (
        <div className={css.vinInput}>
            {decodingVin ? <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> : null}
            {error}
            {vinExistsError}
            <p className={css.vinHeader}>{strings.VinHandy}</p>
            <div className={css.vinLabel}>
                <Link className={vinLinkStyles} onClick={whereHandler}>
                    <div onClick={(e: any) => interactionHandler(e)}>{strings.WhereIsMyVIN}</div>
                </Link>
                <TextField
                    id='vinTextField'
                    label={strings.VIN}
                    value={localVin}
                    type='text'
                    onChange={vinChangedHandler}
                    onBlur={handleVinBlur}
                    onKeyPress={(e) => onKeyPressHandler(e.key)}
                    size='full'
                    variant='VIN'
                    disabled={false}
                />
                <WhereIsMyVin
                    whereOpen = {props.dialogOpen}
                    whereHandler={(e: any) => {
                        whereHandler();
                        interactionHandler(e);
                    }}
                    theme={props.theme}
                />
            </div>
        </div>
    );
};

function GetVinExistsErrorMessage(closeHandler: () => void, loadVehicleHandler: () => void) {
    const headerMsg = strings.VinCreateError;
    const errorMsg = strings.VinExistsApptMsg;
    const primaryMessage = strings.Yes;
    const secondaryMessage = strings.Cancel;

    return (
        <ErrorMessage
            headerMsg={headerMsg}
            errorMsg={errorMsg}
            primaryBtnMessage={primaryMessage}
            secondaryBtnMessage={secondaryMessage}
            primaryHandler={loadVehicleHandler}
            secondaryHandler={closeHandler}
            closeHandler={closeHandler}
        />
    );
}

export default VinInput;